/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * LLM Request Processing
 * 
 * Handles LLM prompt requests for issues, releases, user stories, products, and help.
 */

import { LLMPromptRequest } from '@shared/types/requests/llm';
import { authenticatedFetch } from '@shared/lib/api';

export type RequestResult = { success: boolean; result?: string; error?: string; executionId?: string };

/**
 * Process an issue request using the "Process Issue Request" prompt
 */
export async function processIssueRequest(request: string, activeProduct?: any): Promise<RequestResult> {
  try {
    const promptRequest: LLMPromptRequest = {
      promptName: 'Process Issue Request',
      params: {
        request: request,
        activeProduct: activeProduct || null
      },
      returnRawText: true,
      velocityRunContext: {
        type: 'quick_request' as const,
        entityType: 'issue' as const,
        entityId: activeProduct?.id ?? 'none',
        productId: activeProduct?.id,
        requestType: 'issue',
      },
    };

    console.log(`[SkillShop Service] Processing issue request:`, { promptRequest });

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    const executionId = response.headers.get('X-Agent-Execution-Id') ?? undefined;

    console.log(`[SkillShop Service] Issue request processed successfully`);
    return {
      success: true,
      result: result,
      executionId,
    };
  } catch (error: any) {
    console.error(`[SkillShop Service] Error processing issue request:`, error);

    return {
      success: false,
      error: error.message || 'Failed to process issue request'
    };
  }
}

/**
 * Process a release request using the "Process Release Request" prompt
 */
export async function processReleaseRequest(request: string, activeProduct?: any): Promise<RequestResult> {
  try {
    const promptRequest: LLMPromptRequest = {
      promptName: 'Process Release Request',
      params: {
        request: request,
        activeProduct: activeProduct || null
      },
      returnRawText: true,
      velocityRunContext: {
        type: 'quick_request' as const,
        entityType: 'release' as const,
        entityId: activeProduct?.id ?? 'none',
        productId: activeProduct?.id,
        requestType: 'release',
      },
    };

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    const executionId = response.headers.get('X-Agent-Execution-Id') ?? undefined;
    return { success: true, result: result, executionId };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to process release request' };
  }
}

/**
 * Process a user story request using the "Process User Story Request" prompt
 */
export async function processUserStoryRequest(request: string, activeProduct?: any): Promise<RequestResult> {
  try {
    const promptRequest: LLMPromptRequest = {
      promptName: 'Process User Story Request',
      params: {
        request: request,
        activeProduct: activeProduct || null
      },
      returnRawText: true,
      velocityRunContext: {
        type: 'quick_request' as const,
        entityType: 'story' as const,
        entityId: activeProduct?.id ?? 'none',
        productId: activeProduct?.id,
        requestType: 'user_story',
      },
    };

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    const executionId = response.headers.get('X-Agent-Execution-Id') ?? undefined;
    return { success: true, result: result, executionId };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to process user story request' };
  }
}

/**
 * Process a product request using the "Process Product Request" prompt
 */
export async function processProductRequest(request: string, activeProduct?: any): Promise<RequestResult> {
  try {
    const promptRequest: LLMPromptRequest = {
      promptName: 'Process Product Request',
      params: {
        request: request,
        activeProduct: activeProduct || null
      },
      returnRawText: true,
      velocityRunContext: {
        type: 'quick_request' as const,
        entityType: 'product' as const,
        entityId: activeProduct?.id ?? 'none',
        productId: activeProduct?.id,
        requestType: 'product',
      },
    };

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    const executionId = response.headers.get('X-Agent-Execution-Id') ?? undefined;
    return { success: true, result: result, executionId };
  } catch (error: any) {
    return { success: false, error: error.message || 'Failed to process product request' };
  }
}

/**
 * Process a help question using the Velocity Guide agent
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function processHelpQuestion(question: string): Promise<RequestResult> {
  try {
    const promptRequest: LLMPromptRequest = {
      promptName: 'velocity-guide',
      params: {
        message: question
      },
      returnRawText: true,
    };

    console.log('[SkillShop Service] Processing help question:', { question });

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    
    console.log('[SkillShop Service] Help question processed successfully');
    return {
      success: true,
      result: result
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error processing help question:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to process help question'
    };
  }
}

/**
 * Generate a commit message based on file changes and diffs
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function generateCommitMessage(
  changedFiles: Array<{ path: string; status: string }>,
  diffs?: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    // Format the changed files for the prompt
    const filesFormatted = changedFiles
      .map(f => `- ${f.path} (${f.status})`)
      .join('\n');

    const promptRequest: LLMPromptRequest = {
      promptName: 'Generate Commit Message',
      params: {
        changedFiles: filesFormatted,
        diffs: diffs || ''
      },
      returnRawText: true,
    };

    console.log('[SkillShop Service] Generating commit message for', changedFiles.length, 'files');

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    
    console.log('[SkillShop Service] Commit message generated successfully');
    return {
      success: true,
      message: result.trim()
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error generating commit message:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to generate commit message'
    };
  }
}

